import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ErrorLogService } from '../services/error-log.service';
import { Router } from '@angular/router';

// A deploy replaces every lazy route chunk with a newly content-hashed
// filename and removes the old ones. A browser tab that was already open
// (sitting on the login page, or just idle) still has the *previous*
// build's main.js loaded, which references the *old* chunk filenames - so
// the first attempt to lazy-load any route whose code changed in that
// deploy fails with exactly one of these messages, depending on browser/
// bundler. Nothing in the UI surfaces this today: the failed
// import()/loadChildren call rejects, Angular's router silently cancels
// the navigation, and the rejection lands here - so every affected button/
// link just looks dead, with no visible error (confirmed live, AL-2906
// follow-up: beta.alinesoft.com, 2026-10-14, ~15 of these in ERROR_LOG_T
// inside a 3-minute window, same tab, same session, every one on a route
// that doesn't match the two the user found to still work - the chunks
// behind those two happened not to change in that particular deploy, so
// their old filename still resolved). A hard refresh fetches the current
// index.html + current main.js, which reference the current filenames -
// that's the only reason it "fixes" anything.
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk [\w.-]+ failed|ChunkLoadError|Importing a module script failed/i;

// Survives the reload we trigger below (sessionStorage, unlike
// sessionStorage-free state, isn't wiped by location.reload()) but starts
// fresh for a new tab/session, so a *future* deploy can still trigger
// exactly one more auto-reload. If the error recurs right after our own
// reload, this stops a second one - that case is a real server/network
// problem, not a stale chunk, and should surface normally instead of
// looping.
const CHUNK_RELOAD_GUARD_KEY = 'al_chunk_reload_attempted';

/**
 * Global error handler that catches unhandled errors and reports them
 * to the backend for permanent storage in the error log database.
 *
 * Includes debounce/dedup logic to prevent flooding the API with
 * the same repeating error.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private recentErrors = new Map<string, number>();
  private readonly DEBOUNCE_MS = 5000; // Suppress same error within 5 seconds

  constructor(private injector: Injector) {}

  handleError(error: any): void {
    // Always log to console for dev tools
    console.error('GlobalErrorHandler caught:', error);

    if (this.handleStaleChunkError(error)) {
      return;
    }

    try {
      const errorMessage =
        error?.message || error?.toString() || 'Unknown error';
      const stackTrace = error?.stack || '';

      // Debounce: skip if we recently reported the same error
      const errorKey = `${errorMessage}`;
      const now = Date.now();
      const lastReported = this.recentErrors.get(errorKey);
      if (lastReported && now - lastReported < this.DEBOUNCE_MS) {
        return;
      }
      this.recentErrors.set(errorKey, now);

      // Clean up old entries periodically
      if (this.recentErrors.size > 100) {
        for (const [key, time] of this.recentErrors) {
          if (now - time > this.DEBOUNCE_MS) {
            this.recentErrors.delete(key);
          }
        }
      }

      // Use injector to avoid circular dependency issues
      const errorLogService = this.injector.get(ErrorLogService);
      let currentUrl = '';
      try {
        const router = this.injector.get(Router);
        currentUrl = router.url;
      } catch {
        currentUrl = window.location.pathname;
      }

      errorLogService.reportError({
        errorName: error?.name || 'UnhandledError',
        errorMessage: errorMessage.substring(0, 5000),
        stackTrace: stackTrace.substring(0, 10000),
        uiUrl: currentUrl,
        additionalInfo: {
          type: 'unhandled_error',
          userAgent: navigator.userAgent
        }
      });
    } catch (reportErr) {
      // Never let error reporting break the app
      console.error('Failed to report error:', reportErr);
    }
  }

  /**
   * Returns true if `error` was a stale-chunk-after-deploy failure and has
   * been dealt with (reported once, then either reloaded or - if we
   * already reloaded once this session and it's still failing - left for
   * the normal reporting path below instead of looping). Returns false for
   * every other error, so handleError's usual flow runs unchanged.
   */
  private handleStaleChunkError(error: any): boolean {
    const errorMessage: string =
      error?.message || (typeof error === 'string' ? error : '') || '';
    if (!CHUNK_LOAD_ERROR_PATTERN.test(errorMessage)) {
      return false;
    }

    let alreadyReloaded = false;
    try {
      alreadyReloaded =
        sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === 'true';
    } catch {
      // sessionStorage can throw in a locked-down/private context - fall
      // back to treating this as a fresh session (one reload attempt,
      // worst case) rather than never auto-recovering at all.
    }

    if (alreadyReloaded) {
      // Already tried this once this session and the same kind of error
      // came back - a real, ongoing problem, not a one-off stale
      // reference. Let it fall through to the normal report-and-log path
      // below instead of reloading (or reporting as this special case)
      // again.
      return false;
    }

    try {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, 'true');
    } catch {
      // If we can't persist the guard, don't risk an unguarded reload loop -
      // fall through to the normal path instead.
      return false;
    }

    let currentUrl = '';
    try {
      currentUrl = this.injector.get(Router).url;
    } catch {
      currentUrl = window.location.pathname;
    }

    try {
      this.injector.get(ErrorLogService).reportError({
        errorName: error?.name || 'ChunkLoadError',
        errorMessage: errorMessage.substring(0, 5000),
        stackTrace: (error?.stack || '').substring(0, 10000),
        uiUrl: currentUrl,
        additionalInfo: {
          type: 'stale_chunk_reload',
          userAgent: navigator.userAgent
        }
      });
    } catch (reportErr) {
      console.error('Failed to report stale-chunk error:', reportErr);
    }

    console.warn(
      'A new version of the app was deployed while this page was open. Reloading to pick it up...'
    );
    this.reloadPage();
    return true;
  }

  /**
   * Split out from handleStaleChunkError so tests can spy on this one
   * method instead of stubbing window.location.reload directly - real
   * Chrome (unlike jsdom) doesn't allow Location.prototype.reload to be
   * redefined, so a spy on the global would either no-op silently or throw
   * "Cannot redefine property: reload", and if it ever did take effect it
   * would trigger a real navigation and tear down the test runner itself.
   */
  protected reloadPage(): void {
    window.location.reload();
  }
}
